import React, { useState } from 'react';
import { SearchBar } from '@/components/shared';
import { useSegmentation } from '@/contexts/SegmentationContext';
import { useLookupAccounts } from '@/generated/customer';
import { useGetTranslations } from '@/generated/translations';
import { useDebounce } from '@/helpers';
import { AccountRadio } from './AccountRadio';
import styles from './MoveModal.module.scss';

/** The endpoint pages; a short list is enough to pick from — narrow the search instead. */
const accountPageSize = 10;

export interface MoveCompositeFieldsProps {
	accountId: number;
	value: number | null;
	onChange: (targetAccountId: number | null) => void;
}

/**
 * Body shown when "move the whole registration to another account" is selected.
 *
 * The backend already restricts the lookup to accounts the registration may
 * move to (active, in the segmentation's business unit, linked to this user),
 * so this only has to exclude the account we are already on.
 */
export const MoveCompositeFields: React.FunctionComponent<MoveCompositeFieldsProps> = ({
	accountId,
	value,
	onChange,
}) => {
	const { segmentationId } = useSegmentation();
	const { data: translations } = useGetTranslations(segmentationId);
	const [phrase, setPhrase] = useState('');
	const debouncedPhrase = useDebounce(phrase, 300);

	// An empty term would return an arbitrary first page, which reads as a
	// suggestion rather than a result — wait until something is typed.
	const hasSearchTerm = debouncedPhrase.trim().length > 0;

	const {
		data: response,
		isLoading,
		isError,
	} = useLookupAccounts(
		segmentationId,
		{
			SearchTerm: debouncedPhrase,
			ExcludeAccountIds: [accountId],
			PageNumber: 1,
			PageSize: accountPageSize,
		},
		{
			query: { enabled: !!segmentationId && hasSearchTerm },
			// Array params serialize as ExcludeAccountIds[0]=… without this.
			axios: { paramsSerializer: { indexes: null } },
		},
	);

	const moveTranslations = translations?.data.composite.moveComposite;
	const accounts = response?.data.accounts;

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPhrase(event.target.value);
		// A pick belongs to the result set it came from. Dropping it as soon as the
		// search changes stops Confirm firing against an account no longer listed.
		onChange(null);
	};

	const renderResults = () => {
		// Nothing searched yet — the search field itself is the only affordance needed.
		if (!hasSearchTerm) {
			return null;
		}

		if (isLoading) {
			return <p className={styles.emptyMessage}>{translations?.data.shared.loading}</p>;
		}

		if (isError) {
			return <p className={styles.emptyMessage}>{translations?.data.shared.genericErrorMsg}</p>;
		}

		if (!accounts?.length) {
			return <p className={styles.emptyMessage}>{moveTranslations?.noAccountsFound}</p>;
		}

		return (
			<ul className={styles.accountList}>
				{accounts.map((account) => (
					<li key={account.id}>
						<AccountRadio
							account={account}
							checked={value === account.id}
							onChange={(event) => onChange(Number(event.target.value))}
						/>
					</li>
				))}
			</ul>
		);
	};

	return (
		<div className={styles.field}>
			<SearchBar
				variant={'inverse'}
				inputId={'move-target-account'}
				placeholder={moveTranslations?.searchAccounts}
				value={phrase}
				onChange={handleSearchChange}
			/>

			{renderResults()}
		</div>
	);
};
